import { queryOptions } from '@tanstack/react-query';
import { notFound } from 'next/navigation';
import ApiMethods from 'shared/api/api-methods';
import type {
  IResourceStatus,
  StatusResponse,
} from 'shared/api/services/types';
import type {
  Autoscaling,
  NotificationsSettings,
} from '@/enteties/notification_settings/modal';
import {
  mutationProjectKeys,
  projectKeys,
} from '@/enteties/project/api/projects.query-key';
import type {
  Project,
  ProjectCreate,
  Projects,
} from '@/enteties/project/modal';
import type { Tariff } from '@/enteties/tariff/modal';
import { tariffKeys } from '@/enteties/tariff/api/tariff.query-key';
import type { TariffFormValues } from 'widgets/project-creation-stepper/model';
import type { IValidateProject } from '@/enteties/config/model';
import type { ConfigFormData } from '@/shared/types/configuration-service';
import { handleMutateApiError } from 'shared/api/utils';

const url = '/projects';

export const allProjects = {
  getAllProjects: async function getAllProjects(): Promise<Projects> {
    const res = await ApiMethods.get(url);
    if (!res.ok) throw new Error(`${res.status}`);
    return await res.json();
  },
  getAllProjectsQueryOptions: () =>
    queryOptions({
      queryKey: projectKeys.allProjects,
      queryFn: () => allProjects.getAllProjects(),
      // staleTime: 5 * 1000,
    }),
};

export const singleProject = {
  getSingleProject: async function getSingleProject(
    slug: string
  ): Promise<Project> {
    const res = await ApiMethods.get(`${url}/${slug}`);
    if (!res.ok) {
      return notFound();
      // throw new Error(`${res.status}`);
    }
    return await res.json();
  },
  getSingleProjectQueryOptions: ({ slug }: { slug: string }) =>
    queryOptions({
      queryKey: projectKeys.project(slug),
      queryFn: () => singleProject.getSingleProject(slug),
      // staleTime: 5 * 1000,
    }),
};

export async function getServiceResource(
  slug: string
): Promise<IResourceStatus> {
  const data = await ApiMethods.get(`${url}/${slug}/service_resources`);
  if (!data.ok) throw Error(`${data.status}`);
  return await data.json();
}

export const deleteProject = async (slug: string): Promise<StatusResponse> => {
  const data = await ApiMethods.delete(`${url}/${slug}`);
  if (!data.ok) {
    return handleMutateApiError(data);
  }
  return await data.json();
};

export const projectCreation = {
  createProject: async function createProject(
    data: TariffFormValues
  ): Promise<ProjectCreate> {
    const res = await ApiMethods.post(url, data);
    if (!res.ok) {
      return handleMutateApiError(res);
    }
    return await res.json();
  },
  createProjectMutationOptions: () => ({
    mutationKey: mutationProjectKeys.creating,
    mutationFn: (data: TariffFormValues) => projectCreation.createProject(data),
  }),
};

// PROJECT_CONFIG

// PROJECT_TARIFF

export const postProjectTariff = async (
  slug: string,
  tariff: number
): Promise<StatusResponse> => {
  const data = await ApiMethods.post(`${url}/${slug}/tariff`, tariff);
  if (!data.ok) throw Error(`${data.status}`);
  return await data.json();
};

export const currentProjectTariff = {
  getProjectTariff: async (slug: string): Promise<Tariff> => {
    const data = await ApiMethods.get(`${url}/${slug}/tariff`);
    if (!data.ok) throw Error(`${data.status}`);
    return await data.json();
  },
  getProjectTariffQueryOptions: ({ slug }: { slug: string }) =>
    queryOptions({
      queryKey: tariffKeys.projectTariff(slug),
      queryFn: () => currentProjectTariff.getProjectTariff(slug),
    }),
};

// PROJECT_NOTIFICATION_SETTINGS

export async function getNotificationSettings(
  slug: string
): Promise<NotificationsSettings> {
  const data = await ApiMethods.get(`${url}/${slug}/notification_settings`);
  if (!data.ok) throw Error(`${data.status}`);
  return await data.json();
}

export async function updateNotificationSettings(
  slug: string,
  data: NotificationsSettings
): Promise<NotificationsSettings> {
  const response = await ApiMethods.post(
    `${url}/${slug}/notification_settings`,
    data
  );
  if (!response.ok) throw Error(`${response.status}`);
  return await response.json();
}

// PROJECT_AUTOSCALING

export async function getAutoScalingSettings(
  slug: string
): Promise<Autoscaling> {
  const data = await ApiMethods.get(`${url}/${slug}/autoscaling`);
  if (!data.ok) throw Error(`${data.status}`);
  return await data.json();
}

export async function updateAutoScalingSettings(
  slug: string,
  data: Autoscaling
): Promise<Autoscaling> {
  const response = await ApiMethods.post(`${url}/${slug}/autoscaling`, data);
  if (!response.ok) throw Error(`${response.status}`);
  return await response.json();
}

// PROJECT CONTROLLER

export const reRunProject = {
  reRun: async (slug: string): Promise<StatusResponse> => {
    const data = await ApiMethods.post(`${url}/${slug}/restart`, {});
    if (!data.ok) throw Error(`${data.status}`);
    return await data.json();
  },
  getReRunProjectMutationOptions: ({ slug }: { slug: string }) => ({
    mutationFn: () => reRunProject.reRun(slug),
  }),
};

export const rebuildProject = async (slug: string): Promise<StatusResponse> => {
  const data = await ApiMethods.post(`${url}/${slug}/rebuild`, {});
  if (!data.ok) {
    return handleMutateApiError(data);
  }
  return await data.json();
};

export const projectScale = {
  scaleProject: async (
    slug: string,
    instances: number
  ): Promise<StatusResponse> => {
    const data = await ApiMethods.post(`${url}/${slug}/scale`, { instances });
    if (!data.ok) throw Error(`${data.status}`);
    return await data.json();
  },
  getScaleProjectMutationOptions: ({ slug }: { slug: string }) => ({
    mutationKey: mutationProjectKeys.scalingProject(slug),
    mutationFn: (instances: number) =>
      projectScale.scaleProject(slug, instances),
  }),
};

export const stopBuild = {
  stopBuild: async (slug: string): Promise<Project> => {
    const data = await ApiMethods.post(`${url}/${slug}/stop-build`, {});
    if (!data.ok) throw Error(`${data.status}`);
    return await data.json();
  },
  getStopBuildMutationOptions: ({ slug }: { slug: string }) => ({
    mutationKey: mutationProjectKeys.stopBuild(slug),
    mutationFn: () => stopBuild.stopBuild(slug),
  }),
};

export const scaleProject = async (
  slug: string,
  instances: number
): Promise<StatusResponse> => {
  const data = await ApiMethods.post(`${url}/${slug}/scale`, { instances });
  if (!data.ok) throw Error(`${data.status}`);
  return await data.json();
};

export const freezeProject = async (slug: string): Promise<StatusResponse> => {
  const data = await ApiMethods.put(`${url}/${slug}/freeze`, {});
  if (!data.ok) throw Error(`${data.status}`);
  return await data.json();
};

// PROJECT_CONFIG VALIDATION

export const validateProjectGet = {
  validateProject: async (slug: string): Promise<IValidateProject> => {
    const data = await ApiMethods.get(`${url}/${slug}/config/validate`);
    if (!data.ok) {
      if (data.status === 404) {
        // @ts-ignore
        return {};
      }
      throw Error(`${data.status}`);
    }
    return await data.json();
  },
  validateProjectQueryOptions: ({ slug }: { slug: string }) =>
    queryOptions({
      queryKey: projectKeys.validateProject(slug),
      queryFn: () => validateProjectGet.validateProject(slug),
    }),
};

export const validateProjectPost = {
  validateProject: async (
    slug: string,
    config: ConfigFormData
  ): Promise<IValidateProject> => {
    const data = await ApiMethods.post(
      `${url}/${slug}/config/validate`,
      config
    );
    if (!data.ok) throw Error(`${data.status}`);
    return await data.json();
  },
  validateProjectMutationOptions: ({ slug }: { slug: string }) => ({
    mutationKey: mutationProjectKeys.validateProject(slug),
    mutationFn: (config: ConfigFormData) =>
      validateProjectPost.validateProject(slug, config),
  }),
};

// Data recovery after the incident (March 21)

export async function recoverProjectData(slug: string) {
  const data = await ApiMethods.put(`${url}/${slug}/recover-data`, {});
  if (!data.ok) throw Error(`${data.status}`);
  return await data.json();
}
